import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientAuth } from '../../contexts/ClientAuthContext';

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #0D2C54 0%, #1A5490 55%, #1a3a6b 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    padding: '24px',
    position: 'relative',
    overflow: 'hidden',
  },
  // Subtle background texture
  pageAccent: {
    position: 'absolute',
    top: '-30%',
    right: '-20%',
    width: '600px',
    height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(212,168,83,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  pageAccent2: {
    position: 'absolute',
    bottom: '-20%',
    left: '-15%',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    background: 'rgba(255,255,255,0.97)',
    borderRadius: '16px',
    padding: '52px 48px 44px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.2)',
    position: 'relative',
    zIndex: 1,
    animation: 'fadeSlideIn 0.5s ease-out',
  },
  logoWrap: {
    textAlign: 'center',
    marginBottom: '28px',
  },
  logo: {
    height: '52px',
    objectFit: 'contain',
  },
  logoFallback: {
    display: 'inline-block',
    height: '52px',
    lineHeight: '52px',
    fontSize: '22px',
    fontWeight: '800',
    color: '#0D2C54',
    letterSpacing: '2px',
  },
  heading: {
    textAlign: 'center',
    marginBottom: '6px',
    fontFamily: '"Georgia", "Times New Roman", serif',
    fontSize: '24px',
    fontWeight: '700',
    color: '#0D2C54',
    letterSpacing: '-0.3px',
  },
  subheading: {
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: '400',
    color: '#8a9ab0',
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    marginBottom: '36px',
  },
  divider: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, #dde3ea, transparent)',
    marginBottom: '32px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '600',
    color: '#5a6a7e',
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '13px 16px',
    border: '1.5px solid #dde3ea',
    borderRadius: '8px',
    fontSize: '15px',
    color: '#0D2C54',
    background: '#fafbfc',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none',
    fontFamily: 'inherit',
  },
  button: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #0D2C54 0%, #1A5490 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    letterSpacing: '0.8px',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'opacity 0.2s, transform 0.1s',
    fontFamily: 'inherit',
  },
  buttonDisabled: {
    opacity: '0.65',
    cursor: 'not-allowed',
  },
  error: {
    background: '#fff5f5',
    border: '1px solid #f5c6c6',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#c62828',
    fontSize: '13px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  footer: {
    textAlign: 'center',
    marginTop: '32px',
    color: 'rgba(255,255,255,0.45)',
    fontSize: '12px',
    letterSpacing: '0.5px',
    position: 'relative',
    zIndex: 1,
  },
  tagline: {
    marginTop: '10px',
    color: 'rgba(212,168,83,0.7)',
    fontSize: '11px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
};

export default function ClientLogin() {
  const navigate = useNavigate();
  const { clientLogin, employeeLogin } = useClientAuth();
  const [arCode, setArCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inputFocus, setInputFocus] = useState(null);
  const [isEmployeeMode, setIsEmployeeMode] = useState(false);

  React.useEffect(() => { document.title = 'ACS Client Portal'; }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (isEmployeeMode) {
      if (!email.trim() || !password) {
        setError('Please enter your email and password.');
        return;
      }
      setLoading(true);
      try {
        await employeeLogin(email, password);
        navigate('/portal');
      } catch (err) {
        const msg = err?.response?.data?.error || 'Invalid email or password.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    } else {
      if (!arCode.trim() || !password) {
        setError('Please enter your account number and password.');
        return;
      }
      setLoading(true);
      try {
        await clientLogin(arCode, password);
        navigate('/portal');
      } catch (err) {
        const msg = err?.response?.data?.error || 'Invalid account number or password.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleMode = () => {
    setIsEmployeeMode(prev => !prev);
    setError('');
    setPassword('');
    setArCode('');
    setEmail('');
  };

  const inputStyle = (field) => ({
    ...styles.input,
    borderColor: inputFocus === field ? '#1A5490' : '#dde3ea',
    boxShadow: inputFocus === field ? '0 0 0 3px rgba(17,68,122,0.1)' : 'none',
    background: inputFocus === field ? '#fff' : '#fafbfc',
  });

  return (
    <>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .login-btn:hover:not(:disabled) {
          opacity: 0.88 !important;
          transform: translateY(-1px) !important;
        }
        .login-btn:active:not(:disabled) {
          transform: translateY(0) !important;
        }
      `}</style>
      <div style={styles.page}>
        <div style={styles.pageAccent} />
        <div style={styles.pageAccent2} />

        <div style={styles.card}>
          <div style={styles.logoWrap}>
            <img
              src="/img/acs-nav-logo.png"
              alt="Audio Command Systems"
              style={styles.logo}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'inline-block';
              }}
            />
            <span style={{ ...styles.logoFallback, display: 'none' }}>ACS</span>
          </div>

          <h1 style={styles.heading}>Audio Command Systems</h1>
          <div style={styles.subheading}>{isEmployeeMode ? 'Staff Access' : 'Client Portal'}</div>

          <div style={styles.divider} />

          {error && <div style={styles.error}>{error}</div>}

          <form onSubmit={handleSubmit} noValidate>
            {isEmployeeMode ? (
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="email">Employee Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setInputFocus('email')}
                  onBlur={() => setInputFocus(null)}
                  style={inputStyle('email')}
                  placeholder="you@audiocommand.com"
                  autoComplete="email"
                  spellCheck={false}
                />
              </div>
            ) : (
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="ar_code">Account Number</label>
                <input
                  id="ar_code"
                  type="text"
                  value={arCode}
                  onChange={(e) => setArCode(e.target.value)}
                  onFocus={() => setInputFocus('ar_code')}
                  onBlur={() => setInputFocus(null)}
                  style={inputStyle('ar_code')}
                  placeholder="e.g. SMIT01"
                  autoComplete="username"
                  autoCapitalize="characters"
                  spellCheck={false}
                />
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setInputFocus('password')}
                onBlur={() => setInputFocus(null)}
                style={inputStyle('password')}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading}
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          {/* Employee / client toggle */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button
              type="button"
              onClick={toggleMode}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#8a9ab0',
                letterSpacing: '0.3px',
                fontFamily: 'inherit',
                padding: '4px 8px',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
            >
              {isEmployeeMode ? '← Back to Client Login' : 'ACS Employee? Sign in with your email'}
            </button>
          </div>
        </div>

        <div style={styles.footer}>
          <div>Audio Command Systems, Inc.</div>
          <div style={styles.tagline}>Smarter By Design</div>
        </div>
      </div>
    </>
  );
}
